import React from "react";
import {
  Paper,
  Typography,
  Tooltip,
  CircularProgress,
  Box,
} from "@mui/material";
import { useOrganisation } from "../../../hooks/useOrganisation";
import { getTextColor } from "../../../helpers";

const getCategoryColor = (category) => {
  if (!category) return null;

  const colors = {
    Elite: "#10B981", // Green
    Strong: "#3B82F6", // Blue
    Proficient: "#F59E0B", // Yellow
    Developing: "#F97316", // Orange
    Struggling: "#EF4444", // Red
    Critical: "#7F1D1D", // Dark Red
  };

  return colors[category] || "#6B7280"; // Default gray
};

const isLightColor = (color) => {
  if (!color) return true;

  const hex = color.replace("#", "");

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance using standard formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// Helper function to get contrasting text color
const getContrastingTextColor = (backgroundColor) => {
  return isLightColor(backgroundColor) ? "#000000" : "#FFFFFF";
};

const formatScore = (score) => {
  if (score === null || score === undefined) return "N/A";
  return `${(score * 100).toFixed(1)}%`;
};

const DataCard = ({
  title,
  count,
  onClick,
  isSelected,
  isActivated,
  unHover,
  subtitle, // New prop for engagement category
  isScore, // New prop to identify score cards
  tooltip, // New prop for tooltip text
  isLoading = false, // New prop for loading state
}) => {
  const { primaryColorLight, primaryColor } = useOrganisation();
  const categoryColor = getCategoryColor(subtitle);

  // Determine the best text color based on background
  const textColor =
    getTextColor(primaryColor) ||
    getContrastingTextColor(primaryColorLight) ||
    "#000000";

  const cardContent = (
    <Paper
      elevation={isSelected && isActivated ? 6 : 3}
      sx={{
        textAlign: "center",
        padding: 2,
        backgroundColor: primaryColorLight,
        cursor: unHover ? "default" : "pointer",
        position: "relative",
        transition: "all 0.2s ease-in-out",
        transform: isSelected && isActivated ? "scale(1.02)" : "scale(1)",
        border: isSelected && isActivated ? "2px solid black" : "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        "&:hover": unHover
          ? {}
          : {
              transform: "scale(1.02)",
              boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            },
        "&::after":
          isSelected && isActivated
            ? {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: "4px",
                boxShadow: "0 0 0 2px black",
                animation: "pulse 2s infinite",
              }
            : {},
        "@keyframes pulse": {
          "0%": {
            transform: "scale(1)",
            opacity: 0.8,
          },
          "50%": {
            transform: "scale(1.02)",
            opacity: 0.4,
          },
          "100%": {
            transform: "scale(1)",
            opacity: 0.8,
          },
        },
        borderLeft:
          isScore && categoryColor ? `4px solid ${categoryColor}` : "none",
      }}
      onClick={onClick}
    >
      <Typography
        variant="h6"
        sx={{
          color: textColor,
          fontWeight: isSelected && isActivated ? 600 : 400,
          transition: "color 0.2s ease-in-out",
        }}
      >
        {title}
      </Typography>

      {isLoading ? (
        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
          <CircularProgress
            size={32}
            sx={{
              color: textColor,
              mt: 1,
              mb: 1,
            }}
          />
        </Box>
      ) : (
        <Typography
          variant="h4"
          sx={{
            mt: 1,
            fontWeight: isSelected && isActivated ? 700 : 500,
            color: textColor,
            transition: "color 0.2s ease-in-out",
          }}
        >
          {isScore ? formatScore(count) : count}
        </Typography>
      )}

      {isScore && subtitle && !isLoading && (
        <Typography
          variant="subtitle2"
          sx={{
            mt: 1,
            color: "#FFFFFF",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            backgroundColor: categoryColor,
            px: 1.5,
            py: 0.5,
            borderRadius: "4px",
            display: "inline-block",
            minWidth: "80px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Paper>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      {cardContent}
    </Tooltip>
  ) : (
    cardContent
  );
};

export default DataCard;
